import { useCallback, useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { ClockIcon, TvIcon } from "@heroicons/react/24/outline";

import AssemblyFlow, {
  CompletedTodaySection,
} from "../../components/productionDashboard/AssemblyFlow";
import BayManagerCard from "../../components/productionDashboard/BayManagerCard";
import BayStatusFilters from "../../components/productionDashboard/BayStatusFilters";
import BaySummaryTiles from "../../components/productionDashboard/BaySummaryTiles";
import BayTimeline from "../../components/productionDashboard/BayTimeline";
import DueNextSection from "../../components/productionDashboard/DueNextSection";
import InProgressSessionsColumn from "../../components/productionDashboard/InProgressSessionsColumn";
import OverviewKpiCard from "../../components/productionDashboard/OverviewKpiCard";
import PartsRemovedKpiCard from "../../components/productionDashboard/PartsRemovedKpiCard";
import TvLineOverlay from "../../components/productionDashboard/TvLineOverlay";
import TvModeOverlay from "../../components/productionDashboard/TvModeOverlay";
import YardLocationCapacityCard from "../../components/productionDashboard/YardLocationCapacityCard";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useLocationGroups from "../../data/useLocationGroups";
import useLocations from "../../data/useLocations";
import useSingleSetting from "../../data/useSingleSetting";
import useProductionCompletedToday from "../../data/useProductionCompletedToday";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import {
  ProductionDashboardAssemblyKind,
  ProductionDashboardView,
} from "../../models/ProductionDashboard";
import {
  BAY_FILTERS,
  BayFilter,
  bayInProgressVehicles,
  bayMatchesFilter,
  bayState,
} from "../../utils/bayCardState";
import classNames from "../../utils/classNames";
import {
  bayTypeForProductionView,
  plannedTodayCount,
  PRODUCTION_VIEW_STORAGE_KEY,
  readStoredProductionView,
} from "../../utils/productionDashboardView";
import {
  parseTvLocationTiles,
  summariseRacking,
  TV_LOCATION_TILES_SETTING_KEY,
} from "../../utils/tvLocationTiles";

const STORAGE_KEY = {
  view: PRODUCTION_VIEW_STORAGE_KEY,
  hideStats: "productionDashboard.hideStats",
};

const readStoredHideStats = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(STORAGE_KEY.hideStats) === "true";
};

export default function ProductionDashboard() {
  const { t } = useTranslation("production");
  const { setInfo } = usePageNameContext();
  const isTme = CheckFeatureFlag("TME");
  const [view, setView] = useState<ProductionDashboardView>(readStoredProductionView);
  const [hideStats, setHideStats] = useState<boolean>(readStoredHideStats);
  const [now, setNow] = useState<DateTime>(DateTime.local());
  const [bayFilter, setBayFilter] = useState<BayFilter>("all");
  const [tvMode, setTvMode] = useState(false);
  const closeTvMode = useCallback(() => setTvMode(false), []);

  // Single source of truth for "completed today" so the KPI card below, the Completed Today
  // section and the TME dashboard's dismantled tile can never disagree.
  const {
    overview,
    data,
    bays,
    inProgressTriages,
    inProgressDepollutions,
    busyVehicleIds,
    completedToday,
  } = useProductionCompletedToday(bayTypeForProductionView(view));
  const { availabilities: locationGroupAvailabilities } = useLocationGroups();
  // Polled for the same reason as the section availabilities: TV mode is left up all day.
  const { locations, vehicleCounts } = useLocations({ refetchCountsInterval: 1000 * 60 * 2 });
  const { singleSetting: tvLocationTilesSetting } = useSingleSetting(TV_LOCATION_TILES_SETTING_KEY);
  const maxLocationTiles = parseTvLocationTiles(tvLocationTilesSetting.data?.data?.value);
  const racking = useMemo(
    () => summariseRacking(locations.data?.data ?? [], vehicleCounts.data?.data ?? []),
    [locations.data, vehicleCounts.data]
  );

  useEffect(() => {
    setInfo({
      name: t("Production Dashboard"),
      desc: t("Live view of vehicles being dismantled across the main line and off line stations"),
    });
  }, [setInfo, t]);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(DateTime.local()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY.view, view);
  }, [view]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY.hideStats, String(hideStats));
  }, [hideStats]);

  const assemblies = useMemo(() => {
    const all = data?.assemblies ?? [];
    // TME customers don't use the Non-Assembly column, so drop it from the flow.
    return isTme ? all.filter((a) => a.name !== "Non-Assembly") : all;
  }, [data, isTme]);
  const kpis = data?.kpis;

  // Each stage column comes from its own customer setting, which the payload signals by including
  // the stage in the ordered assembly list. With a stage in the flow the lanes already show every
  // session for it (triage in all of them, depollution in its own line or in all of them when it
  // has none yet), so that stage's panel below is dropped rather than repeating what's on screen.
  const triageInFlow = useMemo(
    () => assemblies.some((a) => a.kind === ProductionDashboardAssemblyKind.Triage),
    [assemblies]
  );
  const depollutionInFlow = useMemo(
    () => assemblies.some((a) => a.kind === ProductionDashboardAssemblyKind.Depollution),
    [assemblies]
  );
  const showTriagePanel = !triageInFlow && inProgressTriages.length > 0;
  const showDepollutionPanel = !depollutionInFlow && inProgressDepollutions.length > 0;

  const isBaysView = view === "Bays";

  // Bay states drive the cards, the filter counts and the summary tiles together. They depend on
  // the ticking clock (a timer crosses its target between refetches), so this recomputes each
  // second - it's a handful of bays, not worth memoising around.
  const bayStates = isBaysView
    ? bays.map((bay) => bayState(bayInProgressVehicles(bay, busyVehicleIds), now))
    : [];
  const filterCounts = BAY_FILTERS.reduce(
    (acc, filter) => {
      acc[filter] = bayStates.filter((s) => bayMatchesFilter(s, filter)).length;
      return acc;
    },
    {} as Record<BayFilter, number>
  );
  const visibleBays = bays.filter((_, i) => bayMatchesFilter(bayStates[i], bayFilter));

  return (
    <div className="mt-2 flex flex-col gap-4">
      {/* Header strip: clock + date + toggles */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border border-gray-200 rounded-md p-3">
        <div className="flex items-center gap-3">
          <ClockIcon className="h-7 w-7 text-gray-400" />
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              {t("Current Time")}
            </div>
            <div className="text-3xl font-semibold text-gray-900 tabular-nums leading-none">
              {now.toFormat("HH:mm:ss")}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="text-sm text-gray-600">{now.toFormat("cccc d LLLL yyyy")}</div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md bg-gray-100 p-0.5 text-xs font-medium">
              <button
                type="button"
                onClick={() => setView("Line")}
                className={classNames(
                  "px-2.5 py-1 rounded",
                  view === "Line" ? "bg-gray-900 text-white" : "text-gray-700"
                )}
              >
                {t("Line")}
              </button>
              <button
                type="button"
                onClick={() => setView("Bays")}
                className={classNames(
                  "px-2.5 py-1 rounded",
                  view === "Bays" ? "bg-gray-900 text-white" : "text-gray-700"
                )}
              >
                {t("Bays")}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setHideStats((p) => !p)}
              className="px-2.5 py-1 rounded border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {hideStats ? t("Show stats") : t("Hide stats")}
            </button>
            <button
              type="button"
              onClick={() => setTvMode(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <TvIcon className="h-3.5 w-3.5" />
              {t("TV mode")}
            </button>
          </div>
        </div>
      </div>

      {tvMode &&
        (isBaysView ? (
          <TvModeOverlay
            bays={bays}
            assemblies={assemblies}
            kpis={kpis}
            now={now}
            busyVehicleIds={busyVehicleIds}
            completedTodayCount={completedToday.length}
            triageCount={inProgressTriages.length}
            depollutionCount={inProgressDepollutions.length}
            hideNonAssembly={isTme}
            onClose={closeTvMode}
          />
        ) : (
          <TvLineOverlay
            bays={bays}
            assemblies={assemblies}
            now={now}
            busyVehicleIds={busyVehicleIds}
            triages={inProgressTriages}
            depollutions={inProgressDepollutions}
            completedTodayCount={completedToday.length}
            locationGroups={locationGroupAvailabilities.data?.data ?? []}
            maxLocationTiles={maxLocationTiles}
            racking={racking}
            triageInFlow={triageInFlow}
            depollutionInFlow={depollutionInFlow}
            onClose={closeTvMode}
          />
        ))}

      {overview.isLoading ? (
        <div className="flex-1 flex items-center justify-center bg-white border border-gray-200 rounded-md py-12">
          <LoadingWheel />
        </div>
      ) : (
        <>
          {/* Stats + yard location capacity side by side */}
          {!hideStats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
              {/* Stats: 2x2 grid in one column (Line), or the bay summary tiles (Bays) */}
              {kpis && isBaysView && (
                <BaySummaryTiles
                  kpis={kpis}
                  bayStates={bayStates}
                  completedTodayCount={completedToday.length}
                />
              )}
              {kpis && !isBaysView && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <OverviewKpiCard
                    label={t("Lines In Use")}
                    value={`${kpis.linesInUse.used} / ${kpis.linesInUse.total}`}
                    footer={t("Production lines in use")}
                  />
                  <OverviewKpiCard
                    label={t("Vehicles In Progress")}
                    value={kpis.vehiclesInProgress}
                    footer={t("Across all stations")}
                  />
                  <OverviewKpiCard
                    label={t("Vehicles Dismantled Today")}
                    value={completedToday.length}
                    footer={t("Completed jobs")}
                  />
                  <PartsRemovedKpiCard partsRemoved={kpis.partsRemoved} />
                  {/* Spans the row on its own when there is no Racking card, so the grid has no gap. */}
                  <div className={classNames("flex", racking.locationCount === 0 && "sm:col-span-2")}>
                    <OverviewKpiCard
                      label={`${t("Planned")} | ${t("Actual")}`}
                      value={
                        <>
                          {plannedTodayCount(bays)}
                          <span className="text-gray-300 mx-2">|</span>
                          {completedToday.length}
                        </>
                      }
                      footer={t("Planned onto the lines today | finished")}
                    />
                  </div>
                  {racking.locationCount > 0 && (
                    <OverviewKpiCard
                      label={t("Racking Spaces")}
                      value={`${racking.used} / ${racking.capacity}`}
                      footer={t("{{count}} free", { count: racking.available })}
                    />
                  )}
                </div>
              )}

              {/* Yard location capacity — click a group to see each location.
                  Wrapper is relative so the card can fill it absolutely, letting
                  the stats column drive the height while the card scrolls inside. */}
              <div className="relative min-h-0">
                <div className="lg:absolute lg:inset-0">
                  <YardLocationCapacityCard
                    locationGroups={locationGroupAvailabilities.data?.data ?? []}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Due next - the bay cards carry their own "Next" line, so Line view only */}
          {!isBaysView && bays.length > 0 && (
            <div>
              <div className="mb-2 flex items-baseline gap-2">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                  {t("Due Onto The Line Next")}
                </h2>
                <span className="text-[11px] text-gray-500">{t("Sourced from the Production Planner")}</span>
              </div>
              <DueNextSection bays={bays} />
            </div>
          )}

          {/* Assembly flow — render every line, including empty ones. A bay works every
              assembly on one vehicle, so a per-assembly lane says nothing useful there. */}
          {!isBaysView && bays.length > 0 && assemblies.length > 0 && (
            <div>
              <div className="mb-2 flex items-baseline gap-2">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                  {t("Assembly Flow")}
                </h2>
                <span className="text-[11px] text-gray-500">
                  {t("Vehicles flow through assemblies left to right")}
                </span>
              </div>
              <AssemblyFlow
                bays={bays}
                assemblies={assemblies}
                now={now}
                busyVehicleIds={busyVehicleIds}
                triages={inProgressTriages}
                depollutions={inProgressDepollutions}
              />
            </div>
          )}

          {/* Bay cards */}
          {isBaysView &&
            (bays.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-md p-8 text-center text-sm text-gray-500">
                {t("No dismantling bays configured.")}
              </div>
            ) : (
              <div>
                <div className="mb-2 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-baseline gap-2">
                    <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                      {t("Bays")}
                    </h2>
                    <span className="text-[11px] text-gray-500">
                      {t("Each bay strips a whole vehicle, assembly by assembly")}
                    </span>
                  </div>
                  <BayStatusFilters value={bayFilter} counts={filterCounts} onChange={setBayFilter} />
                </div>
                {visibleBays.length === 0 ? (
                  <div className="bg-white border border-gray-200 rounded-md p-8 text-center text-sm text-gray-500">
                    {t("No bays match this filter.")}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {visibleBays.map((bay) => (
                      <BayManagerCard
                        key={bay.id}
                        bay={bay}
                        assemblies={assemblies}
                        now={now}
                        busyVehicleIds={busyVehicleIds}
                        hideNonAssembly={isTme}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}

          {/* In-progress Triage / Depollution timers (driven by JobAssemblySession), for each
              stage the customer keeps out of the flow. An empty panel says nothing, so it's hidden. */}
          {(showTriagePanel || showDepollutionPanel) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {showTriagePanel && (
                <InProgressSessionsColumn
                  title={t("Triage In Progress")}
                  sessions={inProgressTriages}
                  now={now}
                  routePrefix="triage"
                />
              )}
              {showDepollutionPanel && (
                <InProgressSessionsColumn
                  title={t("Depollution In Progress")}
                  sessions={inProgressDepollutions}
                  now={now}
                  routePrefix="depollution"
                />
              )}
            </div>
          )}

          {/* Vehicles completed today, gathered from every line/bay */}
          {bays.length > 0 && (
            <CompletedTodaySection vehicles={completedToday} />
          )}

          {isBaysView && bays.length > 0 && (
            <BayTimeline bays={bays} entries={data?.timeline ?? []} now={now} />
          )}
        </>
      )}
    </div>
  );
}
